import { Modal, Input } from "antd";
import { useRef } from "react";
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import CommonDatePicker from "@/common/elements/DatePicker";
import { isNonEmptyVariable } from "@/util/common_function";

/* 
Sample Usage:
showDialog(
  "confirm",
  {
  title: "Title",
  onConfirm: (prompt) => handleConfirm,
  onCancel: () => console.log("Cancelled!")
  });
*/

const useDialog = () => {
  const promptValueRef = useRef("");
  const errorRef = useRef(""); // Error message reference

  const showDialog = (
    type = "info",
    {
      title = "",
      content = "",
      onConfirm = null,
      onCancel = null,
      prompt = "",
      promptType = "text",
      promptValue = "",
      showReason = false,
      reasonMessage = "",
      confirmButtonText = "Yes",
      cancelButtonText = "No",
      requiredMessage = "This field is required",
      showIcon = false,
      // Renders OK(confirm) before Cancel — antd's own default footer order
      // is Cancel-then-OK (matches every other confirm dialog in the app),
      // but some legacy VBScript MsgBox prompts this dialog is standing in
      // for used Yes-then-No instead. Purely a left-to-right visual swap:
      // onConfirm/onCancel keep their normal meaning, only which button
      // renders first changes.
      reverseButtonOrder = false,
    },
  ) => {
    if (onConfirm && typeof onConfirm !== "function") {
      console.error("onConfirm must be a function");
      return;
    }
    if (onCancel && typeof onCancel !== "function") {
      console.error("onCancel must be a function");
      return;
    }

    if (isNonEmptyVariable(promptValue)) {
      promptValueRef.current = promptValue;
    }

    const handleMainCancel = () => {
      if (showReason) {
        Modal.info({
          title: "Cancelled by user",
          content: reasonMessage ? <p>{reasonMessage}</p> : null,
          cancelText: null,
          closable: true,
          mask: false,
          maskClosable: false,
          centered: true,
          className: "custom-confirm-dialog-cancelled",
          onCancel: () => {
            if (onCancel) onCancel(reasonMessage);
            Modal.destroyAll();
          },
        });
      } else {
        if (onCancel) {
          const reason = prompt
            ? promptValueRef.current.trim() || reasonMessage
            : undefined;
          onCancel(reason);
        }
        promptValueRef.current = "";
      }
      handleAfterClose();
    };

    const handleOk = () => {
      if (prompt && !promptValueRef.current.trim()) {
        errorRef.current = requiredMessage;
        showDialog(type, {
          title,
          content,
          onConfirm,
          onCancel,
          prompt,
          showReason,
          reasonMessage,
          confirmButtonText,
          cancelButtonText,
          requiredMessage,
          showIcon,
        });
        return;
      }
      if (onConfirm) {
        onConfirm(prompt ? promptValueRef.current : undefined);
        handleAfterClose();
      }
    };

    const handleAfterClose = () => {
      promptValueRef.current = "";
      errorRef.current = "";
    };

    const icons = {
      info: (
        <InfoCircleOutlined style={{ color: "#1890ff", marginTop: "4px" }} />
      ),
      success: (
        <CheckCircleOutlined style={{ color: "green", marginTop: "4px" }} />
      ),
      error: <CloseCircleOutlined style={{ color: "red", marginTop: "4px" }} />,
      warning: (
        <WarningOutlined style={{ color: "orange", marginTop: "4px" }} />
      ),
      confirm: (
        <ExclamationCircleOutlined
          style={{ color: "#faad14", marginTop: "4px" }}
        />
      ),
    };

    const modalConfig = {
      title,
      icon: showIcon ? icons[type] || icons.info : null,
      content: (
        <div>
          <p>{content}</p>
          {prompt && (
            <div>
              <label style={{ marginBottom: "4px" }}>{prompt}</label>
              {promptType === "text" && (
                <Input
                  defaultValue={promptValueRef.current}
                  onChange={(e) => {
                    promptValueRef.current = e.target.value;
                    errorRef.current = ""; // Clear error on input
                  }}
                  placeholder="Enter value"
                />
              )}
              {promptType === "datetime" && (
                <CommonDatePicker
                  placeholder="MM/DD/YYYY HH:mm"
                  format="MM/DD/YYYY HH:mm"
                  showTime={true}
                  onChange={(e) => {
                    promptValueRef.current = e.target.value;
                    errorRef.current = ""; // Clear error on input
                  }}
                />
              )}

              {errorRef.current && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  {errorRef.current}
                </p>
              )}
            </div>
          )}
        </div>
      ),
      centered: true,
      maskClosable: false,
      mask: false,
      onOk: handleOk,
      onCancel: handleMainCancel,
      className: "custom-confirm-dialog",
      okText: confirmButtonText,
      cancelText: cancelButtonText,
      afterClose: handleAfterClose,
      ...(reverseButtonOrder && {
        footer: (_, { OkBtn, CancelBtn }) => (
          <>
            <OkBtn />
            <CancelBtn />
          </>
        ),
      }),
    };

    if (type === "confirm") {
      Modal.confirm(modalConfig);
    } else if (["info", "success", "error", "warning"].includes(type)) {
      Modal[type](modalConfig);
    } else {
      console.error("Invalid modal type, defaulting to 'info'");
      Modal.info(modalConfig);
    }
  };

  return { showDialog };
};

export default useDialog;
